import React from 'react';
import { Select } from 'antd';
import classnames from 'classnames';
import './DropDown.scss';
import { ReactComponent as BlackChevronDown } from 'style/_assets/icons/black-chevron-down.svg';
import { ReactComponent as BlueChevronDown } from 'style/_assets/icons/blue-chevron-down.svg';
import { ReactComponent as ErrorLogo } from 'style/_assets/icons/error.svg';
import Label from 'components/form/Label/Label';
import OText from 'components/CText/OText';
import { fixADAElements } from './DropdownADA.helper';
import { useAddAccesibilityAttributesToAll } from 'hook/helper.hook';

const { Option } = Select;

function BottomError({ error }) {
  return (
    error &&
    typeof error === 'string' &&
    error.length && (
      <div className="bottomError margT4">
        <ErrorLogo aria-label={'ErrorLogo'} />
        <Label type="caption" className="errorText" role={'alert'}>
          {error}
        </Label>
      </div>
    )
  );
}

function DropDown({
  datatestid,
  placeholder,
  disabled,
  error,
  list,
  internalPlaceholder,
  noSelectPlaceholder,
  isEmptyState,
  hideTopPlaceholder,
  fullWidth,
  isValueLabelBased,
  outerClassName,
  isButtonType,
  prefixText,
  dropdownRender,
  role = 'combobox',
  ...props
}) {
  let varProps = {};
  if (isEmptyState) {
    varProps.placeholder = noSelectPlaceholder
      ? noSelectPlaceholder
      : placeholder;
    varProps.className = 'isEmpty';
  } else {
    varProps.value = props.value;
  }

  const noTopPlaceholderClasses = classnames({
    ' noTopPlaceholder ': hideTopPlaceholder || isButtonType,
  });

  const verticalLineClasses = classnames({
    ' verticalLine ': !hideTopPlaceholder,
    ' verticalLineNoTopPlaceHolder ': hideTopPlaceholder || isButtonType,
  });

  const fullWidthClass = fullWidth ? 'fullWidth' : '';

  const defaultSuffixIcon = (
    <div className="flex-container justify-center items-align-center">
      <div className="divider margR8" />
      <BlackChevronDown aria-label={'BlackChevronDown'} />
    </div>
  );

  const labelAttribute = props?.label ?? placeholder;

  useAddAccesibilityAttributesToAll(
    `div.ant-select[label="${labelAttribute}"] div.ant-select-selector`,
    [
      {
        attribute: 'aria-label',
        value: labelAttribute,
      },
      {
        attribute: 'role',
        value: 'combobox',
      },
      {
        attribute: 'aria-expanded',
        value: !!props.value ?? false,
      },
      {
        attribute: 'id',
        value: props.id + '-selecto',
      },
      {
        attribute: 'aria-controls',
        value: props.id + '-selecto',
      },
    ],
  );

  const accessibleLabel = internalPlaceholder ?? props.id;

  useAddAccesibilityAttributesToAll(
    `div.drop-down-select > div.ant-select.${props.id}`,
    [
      {
        attribute: 'aria-label',
        value: accessibleLabel + '-dropdown-select',
      },
    ],
  );

  useAddAccesibilityAttributesToAll(
    `div.drop-down-select > div.ant-select.${props.id} input.ant-select-selection-search-input`,
    [
      {
        attribute: 'aria-label',
        value: accessibleLabel + 'input-select',
      },
    ],
  );

  const suffixIcon = isButtonType ? (
    <BlueChevronDown aria-label={'BlackChevronDown'} />
  ) : (
    defaultSuffixIcon
  );
  const dropdownRenderCustom =
    dropdownRender ??
    (menu => {
      fixADAElements({ id: props?.id, list, value: props?.value });
      return menu;
    });
  return (
    <>
      <div
        className={classnames(
          `drop-down-select floating-label`,
          {
            disabled: disabled,
            error: error,
          },
          fullWidthClass,
          outerClassName,
        )}
        aria-hidden={disabled ? 'true' : 'false'}
      >
        {prefixText && (
          <div className="prefixText text-16 fontBlue100">{prefixText}</div>
        )}
        <Select
          {...props}
          placeholder={internalPlaceholder}
          {...varProps}
          data-testid={datatestid ? `${datatestid}-Select` : null}
          disabled={disabled ? true : null}
          className={`${props.id} ${noTopPlaceholderClasses}`}
          suffixIcon={suffixIcon}
          dropdownRender={dropdownRenderCustom}
          role={role}
          aria-haspopup="listbox"
          aria-expanded={!!props.value ?? false}
          id={props.id}
          aria-controls={props.id}
          tabIndex="0"
        >
          {list || list?.length ? (
            list.map((v, i) => (
              <Option
                key={i}
                data-testid={datatestid ? `${datatestid}-Option-${v}` : null}
                value={isValueLabelBased ? v.value : v}
                id={placeholder}
                role="option"
              >
                <OText text={isValueLabelBased ? v.label : v} />
              </Option>
            ))
          ) : (
            <Option
              data-testid={datatestid ? `${datatestid}-Option-Empty` : null}
            />
          )}
        </Select>
        {!hideTopPlaceholder && (
          <label className={isEmptyState ? 'inactive' : ''} htmlFor={props.id}>
            {placeholder}
          </label>
        )}
        <div className={`${verticalLineClasses}`}></div>
      </div>
      {error ? <BottomError error={error} /> : null}
    </>
  );
}

export { DropDown };
